import { Children, isValidElement, type ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert } from '@mantine/core';
import { clsx } from 'clsx';

import { STATUS_ICON_MAP } from './alert-banner-icons';
import styles from './AlertBanner.module.css';
import { AlertBannerActions } from './AlertBannerActions';

import type { AlertBannerProps } from './alert-banner-types';

const extractActions = (children: ReactNode) =>
  Children.toArray(children).find((child) => isValidElement(child) && child.type === AlertBannerActions) ?? null;

const extractContent = (children: ReactNode) =>
  Children.toArray(children).filter((child) => !(isValidElement(child) && child.type === AlertBannerActions));

export const AlertBanner = ({
  status,
  description,
  title,
  fullWidth = false,
  stacked = true,
  onDismiss,
  icon,
  classNames,
  children,
}: AlertBannerProps) => {
  const isCompact = !fullWidth && !stacked;
  const resolvedIcon = icon ?? <FontAwesomeIcon icon={STATUS_ICON_MAP[status]} />;
  const actions = extractActions(children);
  const extraContent = extractContent(children);

  const mergedClassNames = {
    root: clsx(styles.alert, classNames?.root),
    icon: clsx(styles.icon, classNames?.icon),
    wrapper: clsx(styles.wrapper, classNames?.wrapper),
    body: clsx(styles.body, classNames?.body),
    closeButton: clsx(styles.closeButton, classNames?.closeButton),
    label: classNames?.label,
    title: classNames?.title,
    message: classNames?.message,
  };

  return (
    <div
      className={styles.root}
      data-status={status}
      data-full-width={fullWidth || undefined}
      data-stacked={!fullWidth && stacked ? true : undefined}
      data-compact={isCompact || undefined}
    >
      <Alert classNames={mergedClassNames} icon={resolvedIcon} title={title} withCloseButton={onDismiss != null} onClose={onDismiss}>
        <div className={styles.content}>
          <div>
            {description}
            {extraContent}
          </div>
          {actions && <div className={classNames?.actionGroup}>{actions}</div>}
        </div>
      </Alert>
    </div>
  );
};

AlertBanner.Actions = AlertBannerActions;
